// ─── ROADMAP ENGINE ───────────────────────────────────────────────────────────
// RoadmapEngine.js — All business logic, localStorage management, no UI code
// Firestore is touched ONLY on: (1) roadmap creation, (2) roadmap completion
// Everything else lives in localStorage — instant, free, offline-capable

import { BADGES, PLANS, CATEGORIES } from "./roadmapContent";
import { db, auth } from "../firebase";
import { doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";

// ─── STORAGE KEYS ─────────────────────────────────────────────────────────────
const KEYS = {
  CONFIG:   "rm_config",    // addiction, plan length, severity, name, why, times, startDate
  PROGRESS: "rm_progress",  // { day_1: { completed, struggled, reflection, urgeCount, completedAt } }
  URGES:    "rm_urges",     // [{ timestamp, day }]
  BADGES:   "rm_badges",    // ["day1", "day7", ...]
  STREAK:   "rm_streak",    // { current, best, lastCompleted }
};

// ─── READ / WRITE HELPERS ─────────────────────────────────────────────────────
const read  = (key)        => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; } };
const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch(e) { console.error("Storage write failed", e); } };
const clear = (key)        => { try { localStorage.removeItem(key); } catch { /* */ } };

// ─── CONFIG ───────────────────────────────────────────────────────────────────
export const getConfig    = ()      => read(KEYS.CONFIG);
export const hasActiveRoadmap = ()  => !!getConfig();

export async function createRoadmap({ addiction, planLength, severity, name, why, wakeTime, sleepTime, triggerType }) {
  const config = {
    addiction,    // e.g. "social_media"
    planLength,   // 21 or 60
    severity,     // "mild" | "moderate" | "severe"
    name,
    why,
    wakeTime,
    sleepTime,
    triggerType,
    startDate: new Date().toISOString(),
    createdAt: Date.now(),
  };

  // Write to localStorage
  write(KEYS.CONFIG,    config);
  write(KEYS.PROGRESS,  {});
  write(KEYS.URGES,     []);
  write(KEYS.BADGES,    []);
  write(KEYS.STREAK,    { current: 0, best: 0, lastCompleted: null });

  // Firestore — ONE write on creation
  try {
    const uid = auth.currentUser?.uid;
    if (uid) {
      await setDoc(doc(db, "users", uid, "roadmap", "active"), {
        addiction, planLength, severity, startDate: config.startDate,
        createdAt: serverTimestamp(),
      });
    }
  } catch (e) {
    console.error("Firestore create error:", e);
    // Non-fatal — localStorage is the source of truth
  }

  return config;
}

export async function completeRoadmap() {
  const config = getConfig();
  if (!config) return;

  // Firestore — write completion record
  try {
    const uid = auth.currentUser?.uid;
    if (uid) {
      const completedAt = serverTimestamp();
      // Move from active to completed
      await setDoc(doc(db, "users", uid, "roadmap", "completed"), {
        ...config,
        completedAt,
        // Auto-delete after 30 days — implement via Firestore TTL or Cloud Function
        deleteAfter: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      });
      await deleteDoc(doc(db, "users", uid, "roadmap", "active"));
    }
  } catch (e) {
    console.error("Firestore complete error:", e);
  }
}

export async function deleteRoadmap() {
  // Clear all localStorage
  Object.values(KEYS).forEach(clear);

  // Clear Firestore
  try {
    const uid = auth.currentUser?.uid;
    if (uid) {
      await deleteDoc(doc(db, "users", uid, "roadmap", "active"));
      await deleteDoc(doc(db, "users", uid, "roadmap", "completed"));
    }
  } catch (e) {
    console.error("Firestore delete error:", e);
  }
}

// ─── PLAN ACCESS ─────────────────────────────────────────────────────────────
export function getPlanDays() {
  const config = getConfig();
  if (!config) return [];
  const planKey = `days${config.planLength}`;
  return PLANS[config.addiction]?.[planKey] ?? [];
}

export function getTodayDay() {
  const config = getConfig();
  if (!config) return 1;
  const start = new Date(config.startDate);
  const now   = new Date();
  const diff  = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  return Math.min(diff + 1, config.planLength);
}

export function getTodayData() {
  const days = getPlanDays();
  const today = getTodayDay();
  return days.find(d => d.day === today) ?? null;
}

export function getDayData(dayNumber) {
  const days = getPlanDays();
  return days.find(d => d.day === dayNumber) ?? null;
}

// ─── PROGRESS ─────────────────────────────────────────────────────────────────
export const getProgress = () => read(KEYS.PROGRESS) || {};

export function getDayProgress(dayNumber) {
  const progress = getProgress();
  return progress[`day_${dayNumber}`] ?? { completed: false, struggled: false, reflection: "", urgeCount: 0, completedAt: null };
}

export function completeDay(dayNumber, reflection = "") {
  const progress = getProgress();
  const key = `day_${dayNumber}`;

  // Don't allow completing future days
  const today = getTodayDay();
  if (dayNumber > today) return;

  progress[key] = {
    ...progress[key],
    completed: true,
    reflection,
    completedAt: new Date().toISOString(),
  };
  write(KEYS.PROGRESS, progress);

  // Update streak
  updateStreak(dayNumber);

  // Check and award badges
  const newBadges = checkBadges(dayNumber);

  // Check if roadmap complete
  const config = getConfig();
  if (dayNumber === config?.planLength) {
    completeRoadmap();
  }

  return { progress: progress[key], newBadges };
}

export function markStruggled(dayNumber) {
  const progress = getProgress();
  const key = `day_${dayNumber}`;
  progress[key] = {
    ...progress[key],
    struggled: true,
    struggledAt: new Date().toISOString(),
  };
  write(KEYS.PROGRESS, progress);

  // Check honesty badge
  const struggledCount = Object.values(progress).filter(d => d.struggled).length;
  const badges = getBadges();
  if (struggledCount >= 3 && !badges.includes("honest")) {
    awardBadge("honest");
    return { newBadge: "honest" };
  }
  return {};
}

// Setback protocol: if relapsed, go back 2 days instead of full restart
export function handleRelapse(dayNumber) {
  const progress = getProgress();
  const config   = getConfig();
  if (!config) return;

  // Roll back to 2 days before current
  const rollbackTo = Math.max(1, dayNumber - 2);
  const newProgress = {};

  // Keep days up to rollback point, mark them as needing redo
  Object.keys(progress).forEach(key => {
    const dayNum = parseInt(key.replace("day_", ""));
    if (dayNum <= rollbackTo) {
      newProgress[key] = progress[key];
    }
  });

  write(KEYS.PROGRESS, newProgress);

  // Reset streak
  const streak = getStreak();
  write(KEYS.STREAK, { ...streak, current: 0 });

  // Update start date so getTodayDay() reflects rollback
  const newStart = new Date();
  newStart.setDate(newStart.getDate() - rollbackTo + 1);
  const updatedConfig = { ...config, startDate: newStart.toISOString() };
  write(KEYS.CONFIG, updatedConfig);

  return { rolledBackTo: rollbackTo };
}

// ─── STREAK ───────────────────────────────────────────────────────────────────
export const getStreak = () => read(KEYS.STREAK) || { current: 0, best: 0, lastCompleted: null };

function updateStreak(dayNumber) {
  const streak = getStreak();
  const today  = new Date().toDateString();

  if (streak.lastCompleted === today) return; // already counted today

  const newCurrent = streak.current + 1;
  write(KEYS.STREAK, {
    current:       newCurrent,
    best:          Math.max(streak.best, newCurrent),
    lastCompleted: today,
  });
}

// ─── URGES ────────────────────────────────────────────────────────────────────
export const getUrges = () => read(KEYS.URGES) || [];

export function logUrge() {
  const urges   = getUrges();
  const today   = getTodayDay();
  const newUrge = { timestamp: Date.now(), day: today };
  urges.push(newUrge);
  write(KEYS.URGES, urges);

  // Check urge warrior badge
  if (urges.length >= 10) {
    const badges = getBadges();
    if (!badges.includes("urge10")) {
      awardBadge("urge10");
      return { newBadge: "urge10" };
    }
  }
  return {};
}

export function getUrgesByDay() {
  const urges = getUrges();
  const byDay = {};
  urges.forEach(u => {
    byDay[u.day] = (byDay[u.day] || 0) + 1;
  });
  return byDay;
}

export function getTodayUrgeCount() {
  const urges = getUrges();
  const today = getTodayDay();
  return urges.filter(u => u.day === today).length;
}

// ─── BADGES ───────────────────────────────────────────────────────────────────
export const getBadges = () => read(KEYS.BADGES) || [];

function awardBadge(badgeId) {
  const badges = getBadges();
  if (!badges.includes(badgeId)) {
    badges.push(badgeId);
    write(KEYS.BADGES, badges);
  }
}

function checkBadges(dayNumber) {
  const newBadges = [];
  const streak    = getStreak();
  const progress  = getProgress();
  const existingBadges = getBadges();

  const award = (id) => {
    if (!existingBadges.includes(id)) {
      awardBadge(id);
      newBadges.push(id);
    }
  };

  // Day milestones
  if (dayNumber >= 1)  award("day1");
  if (dayNumber >= 3)  award("day3");
  if (dayNumber >= 7)  award("day7");
  if (dayNumber >= 14) award("day14");
  if (dayNumber >= 21) award("day21");
  if (dayNumber >= 30) award("day30");
  if (dayNumber >= 45) award("day45");
  if (dayNumber >= 60) award("day60");

  // Streak badges
  const currentStreak = streak.current + 1; // +1 because we just completed one
  if (currentStreak >= 5)  award("streak5");
  if (currentStreak >= 10) award("streak10");

  // Reflection badge — count days with reflection text
  const reflectionCount = Object.values(progress).filter(d => d.reflection && d.reflection.length > 5).length;
  if (reflectionCount >= 7) award("reflect7");

  return newBadges;
}

// ─── STATS ────────────────────────────────────────────────────────────────────
export function getStats() {
  const config   = getConfig();
  const progress = getProgress();
  const streak   = getStreak();
  const urges    = getUrges();
  const badges   = getBadges();

  if (!config) return null;

  const completedDays    = Object.values(progress).filter(d => d.completed).length;
  const totalDays        = config.planLength;
  const progressPercent  = Math.round((completedDays / totalDays) * 100);
  const today            = getTodayDay();
  const daysRemaining    = Math.max(0, totalDays - today);
  const totalUrgesLogged = urges.length;

  // Urge trend: compare first week vs latest week
  const firstWeekUrges  = urges.filter(u => u.day <= 7).length;
  const latestWeekUrges = urges.filter(u => u.day >= today - 7 && u.day <= today).length;
  const urgeTrend       = latestWeekUrges < firstWeekUrges ? "improving" : latestWeekUrges === firstWeekUrges ? "stable" : "increasing";

  // Phase
  const days = getPlanDays();
  const todayData = days.find(d => d.day === today);

  return {
    config,
    completedDays,
    totalDays,
    progressPercent,
    today,
    daysRemaining,
    currentStreak: streak.current,
    bestStreak:    streak.best,
    totalUrgesLogged,
    urgeTrend,
    badges,
    currentPhase:  todayData?.phase ?? "Awareness",
    phaseColor:    todayData?.phaseColor ?? "#6366f1",
  };
}

// ─── CATEGORY / ADDICTION HELPERS ────────────────────────────────────────────
export function getCategoryForAddiction(addictionId) {
  return CATEGORIES.find(cat => cat.addictions.some(a => a.id === addictionId));
}

export function getAddictionMeta(addictionId) {
  for (const cat of CATEGORIES) {
    const found = cat.addictions.find(a => a.id === addictionId);
    if (found) return { ...found, category: cat };
  }
  return null;
}

// ─── HOURS FREE COUNTER ───────────────────────────────────────────────────────
// Real-time "X hours since you started" counter
export function getHoursFree() {
  const config = getConfig();
  if (!config) return 0;
  const start = new Date(config.startDate);
  const now   = new Date();
  return Math.floor((now - start) / (1000 * 60 * 60));
}

export function getDaysFree() {
  return Math.floor(getHoursFree() / 24);
}

// ─── UPGRADE TO 60-DAY ───────────────────────────────────────────────────────
export function upgrade60Day() {
  const config = getConfig();
  if (!config || config.planLength !== 21) return false;

  const updatedConfig = { ...config, planLength: 60, upgradedAt: new Date().toISOString() };
  write(KEYS.CONFIG, updatedConfig);
  return true;
}

// ─── EXPORT ALL DATA (for user transparency) ──────────────────────────────────
export function exportData() {
  return {
    config:   getConfig(),
    progress: getProgress(),
    urges:    getUrges(),
    badges:   getBadges(),
    streak:   getStreak(),
    stats:    getStats(),
    exportedAt: new Date().toISOString(),
  };
}